import { useEffect } from 'react';
import { io } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';

import { StatusFile } from '@/utils/enum';
import { RoomKey, SOCKET_EVENTS, SOCKET_MANAGER_EVENTS } from '@/types';
import { getRoom, socketConfig } from '@/utils/socket';
import { QUERY_KEYS } from '@/utils/query-keys';
import type { BeraClipStatusEvent, BeraSummaryResultV2 } from '@/types/Bera';

import { patchClipStatus } from '../utils';

interface UseBeraClipStatusParams {
	organization_id: string;
	company_id: string;
	job_summary_id: string;
}

export function useBeraClipStatus({ organization_id, company_id, job_summary_id }: UseBeraClipStatusParams): void {
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!organization_id || !company_id || !job_summary_id) {
			return;
		}

		const queryKey = [QUERY_KEYS.GET_BERA_SUMMARY_RESULT_V2];

		function handleClipStatus(event: BeraClipStatusEvent): void {
			if (event.bera_job_summary_id !== job_summary_id) {
				return;
			}

			queryClient.setQueriesData<BeraSummaryResultV2>(queryKey, (summary) => patchClipStatus(summary, event));

			if (event.file.status !== StatusFile.PROCESSED) {
				return;
			}

			queryClient.invalidateQueries(queryKey);
		}

		function handleReconnect(): void {
			queryClient.invalidateQueries(queryKey);
		}

		const room = getRoom(RoomKey.REPORT_STATUS);
		const config = socketConfig(organization_id, company_id);
		const socket = io(room, config);

		socket.on(SOCKET_EVENTS.BERA_CLIP_STATUS, handleClipStatus);
		socket.io.on(SOCKET_MANAGER_EVENTS.RECONNECT, handleReconnect);

		return () => {
			socket.io.off(SOCKET_MANAGER_EVENTS.RECONNECT, handleReconnect);
			socket.disconnect();
		};
	}, [organization_id, company_id, job_summary_id, queryClient]);
}
